if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onSubscriptionPriceChange = this.handleSubscriptionPriceChange.bind(this);
        this.addEventListener('subscription-price:change', this.onSubscriptionPriceChange);

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.cancelQuantityRulesRequest();
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        this.removeEventListener('subscription-price:change', this.onSubscriptionPriceChange);
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedVariant } }) {
        if (!this.contains(event.target)) return;

        this.cancelQuantityRulesRequest();
        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        if (!selectedVariant) {
          this.variantSelectors?.resolvePendingSelectPromise(null);
          this.setUnavailable();
          return;
        }

        this.syncLocalVariantState(productUrl, selectedVariant);
        this.variantSelectors?.resolvePendingSelectPromise(selectedVariant, this.variantSelectors);

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedVariant.id, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl, selectedVariant),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const sourceProductInfo = html.querySelector(selector);
          const variant = this.getSelectedVariant(sourceProductInfo);
          this.variantSelectors?.resolvePendingSelectPromise(variant, this.getVariantSelects(sourceProductInfo));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
            this.variantSelectors?.rejectPendingSelectPromise(error);
          });
      }

      parseJsonScript(parent, selector) {
        try {
          return JSON.parse(parent?.querySelector(selector)?.textContent);
        } catch {
          return null;
        }
      }

      getVariantSelects(queryRoot) {
        return queryRoot?.querySelector('variant-selects');
      }

      getSelectedVariant(productInfoNode) {
        return this.parseJsonScript(this.getVariantSelects(productInfoNode), '[data-selected-variant]');
      }

      buildRequestUrlWithParams(url, variantId, shouldFetchFullPage = false) {
        const requestUrl = new URL(url, window.location.origin);
        if (!shouldFetchFullPage) requestUrl.searchParams.set('section_id', this.sectionId);
        if (variantId) requestUrl.searchParams.set('variant', variantId);
        return requestUrl.toString();
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl, fallbackVariant) {
        return (html) => {
          const sourceVariantSelects = this.getVariantSelects(html);
          const variant = this.getSelectedVariant(html) || fallbackVariant;

          // Resolve product:select promise before updateOptionValues replaces the variant-selects DOM element
          this.variantSelectors?.resolvePendingSelectPromise(variant, sourceVariantSelects);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          this.updateTrendPanels(html);
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.syncSubscriptionWidget(html);
          this.syncPurchaseControls(html);

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateTrendPanels(html) {
        const destinations = [...this.querySelectorAll('[data-trend-panel-block]')];
        html.querySelectorAll('[data-trend-panel-block]').forEach((source) => {
          const destination = destinations.find(
            (candidate) => candidate.dataset.trendPanelBlock === source.dataset.trendPanelBlock
          );
          if (destination) destination.replaceWith(source.cloneNode(true));
        });
      }

      syncPurchaseControls(html) {
        const sourceSubmit = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
        const destinationSubmit = this.querySelector(`#ProductSubmitButton-${this.dataset.section}`);
        const sourceButtonPrice = sourceSubmit?.querySelector('.main-atc-price');
        const destinationButtonPrice = destinationSubmit?.querySelector('.main-atc-price');

        if (sourceButtonPrice && destinationButtonPrice) {
          destinationButtonPrice.innerHTML = sourceButtonPrice.innerHTML;
          destinationButtonPrice.className = sourceButtonPrice.className;
        }

        this.productForm?.toggleSubmitButton(
          sourceSubmit?.hasAttribute('disabled') ?? true,
          window.variantStrings.soldOut
        );

        const purchaseDisabled = sourceSubmit?.hasAttribute('disabled') ?? true;
        this.querySelectorAll(
          `#SectionAtcBtn-${this.dataset.section}, #StickyAtcButton-${this.dataset.section}`
        ).forEach((purchaseButton) => {
          purchaseButton.toggleAttribute('disabled', purchaseDisabled);
          purchaseButton.setAttribute(
            'aria-disabled',
            purchaseDisabled ? 'true' : 'false'
          );
        });
      }

      syncSubscriptionWidget(html) {
        const source = html.querySelector('theme-subscription-widget');
        const destination = this.querySelector('theme-subscription-widget');
        if (!source || !destination) return;

        const selectedType = destination.querySelector('.subscription__box[data-selected="true"]')?.dataset.type;
        const selectedPlanId = destination.querySelector('input[name="selling_plan"]')?.value
          || destination.querySelector('[data-selling-plan-id]:checked')?.dataset.sellingPlanId
          || destination.querySelector('select [data-selling-plan-id]:checked')?.dataset.sellingPlanId;
        const replacement = source.cloneNode(true);

        if (selectedType) {
          replacement.querySelectorAll('.subscription__box').forEach((box) => {
            const active = box.dataset.type === selectedType;
            box.dataset.selected = String(active);
            box.classList.toggle('subscription__box--selected', active);
          });
        }

        if (selectedPlanId) {
          const escapedPlanId = window.CSS?.escape ? CSS.escape(String(selectedPlanId)) : String(selectedPlanId).replace(/"/g, '\\"');
          const matchingControl = replacement.querySelector(`[data-selling-plan-id="${escapedPlanId}"]`);
          if (matchingControl) {
            if (matchingControl.tagName === 'OPTION') matchingControl.selected = true;
            else if ('checked' in matchingControl) matchingControl.checked = true;
          }
        }

        destination.replaceWith(replacement);
      }

      handleSubscriptionPriceChange(event) {
        const widget = event.target.closest('theme-subscription-widget');
        if (!widget || widget.dataset.updatePrices !== 'true') return;

        const { price, comparePrice, amountSaved, percentageSaved } = event.detail || {};
        if (!Number.isFinite(price) || !Number.isFinite(comparePrice)) return;

        const moneyFormat = widget.dataset.moneyFormat || this.variantSelectors?.dataset.moneyFormat || '${{amount}}';
        this.updateDisplayedPrices({ price, comparePrice, amountSaved, percentageSaved, moneyFormat });
      }

      formatMoney(value, moneyFormat) {
        if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(value, moneyFormat);
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: window.Shopify?.currency?.active || this.variantSelectors?.dataset.currencyCode || 'USD',
        }).format(value / 100);
      }

      updateDisplayedPrices({ price, comparePrice, amountSaved, percentageSaved, moneyFormat }) {
        const formatMoney = (value) => this.formatMoney(value, moneyFormat);
        const priceRoot = this.querySelector(`#price-${this.dataset.section} .price`);
        if (priceRoot) {
          const onSale = comparePrice > price;
          priceRoot.classList.toggle('price--on-sale', onSale);
          priceRoot.querySelectorAll('.price__regular .price-item--regular, .price__sale .price-item--sale')
            .forEach((node) => { node.textContent = formatMoney(price); });
          priceRoot.querySelectorAll('.price__compare-price .price-item--regular')
            .forEach((node) => { node.textContent = formatMoney(comparePrice); });
          priceRoot.querySelectorAll('.badge__percentage-saved')
            .forEach((node) => { node.textContent = String(percentageSaved || 0); });
          priceRoot.querySelectorAll('.badge__money-saved')
            .forEach((node) => { node.textContent = formatMoney(amountSaved || 0); });
        }

        this.querySelectorAll(`.main-atc-price-span-${this.dataset.section}`)
          .forEach((node) => { node.textContent = formatMoney(price); });
      }

      syncLocalVariantState(productUrl, variant) {
        const price = Number(variant.price);
        const comparePrice = Number(variant.compare_at_price || variant.price);
        const amountSaved = Math.max(comparePrice - price, 0);
        const percentageSaved = comparePrice > price ? Math.round((amountSaved / comparePrice) * 100) : 0;
        const moneyFormat = this.variantSelectors?.dataset.moneyFormat || '${{amount}}';

        if (Number.isFinite(price) && Number.isFinite(comparePrice)) {
          this.updateDisplayedPrices({ price, comparePrice, amountSaved, percentageSaved, moneyFormat });
          this.querySelector(`#price-${this.dataset.section}`)?.classList.remove('hidden');
        }

        if (this.variantSelectors) {
          this.variantSelectors.dataset.selectedPriceAmount = Number.isFinite(price) ? String(price / 100) : '';
        }

        this.updateVariantInputs(variant.id);
        this.updateURL(productUrl, variant.id);
        this.syncLocalPurchaseControls(variant);
      }

      syncLocalPurchaseControls(variant) {
        const purchaseDisabled = !variant?.available;
        this.productForm?.toggleSubmitButton(
          purchaseDisabled,
          purchaseDisabled ? window.variantStrings.soldOut : undefined
        );

        this.querySelectorAll(
          `#SectionAtcBtn-${this.dataset.section}, #StickyAtcButton-${this.dataset.section}`
        ).forEach((purchaseButton) => {
          purchaseButton.toggleAttribute('disabled', purchaseDisabled);
          purchaseButton.setAttribute('aria-disabled', purchaseDisabled ? 'true' : 'false');
        });
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (!input) return;
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        const productUrl = new URL(url, window.location.origin);
        if (variantId) productUrl.searchParams.set('variant', variantId);
        else productUrl.searchParams.delete('variant');

        this.querySelector('share-button')?.updateUrl(
          productUrl.toString()
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${productUrl.pathname}${productUrl.search}${productUrl.hash}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      cancelQuantityRulesRequest() {
        this.quantityRulesRequestId = (this.quantityRulesRequestId || 0) + 1;
        this.quantityRulesAbortController?.abort();
        this.quantityRulesAbortController = null;
        this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.add('hidden');
      }

      fetchQuantityRules() {
        // Coalesce notifications in the same turn, not separate cart mutations.
        if (this.quantityRulesScheduled) return this.quantityRulesScheduled;
        this.cancelQuantityRulesRequest();
        this.quantityRulesScheduled = Promise.resolve().then(() => {
          this.quantityRulesScheduled = null;
          if (!this.isConnected) return;
          return this.requestQuantityRules();
        });
        return this.quantityRulesScheduled;
      }

      requestQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;
        const sectionId = this.dataset.section;
        const productUrl = this.dataset.url;
        const requestId = this.quantityRulesRequestId;
        const controller = new AbortController();
        this.quantityRulesAbortController = controller;
        const loadingSpinner = this.querySelector('.quantity__rules-cart .loading__spinner');
        loadingSpinner?.classList.remove('hidden');
        return fetch(`${productUrl}?variant=${currentVariantId}&section_id=${sectionId}`, { signal: controller.signal })
          .then((response) => {
            if (!response.ok) throw new Error('Unable to refresh quantity rules');
            return response.text();
          })
          .then((responseText) => {
            if (controller.signal.aborted || requestId !== this.quantityRulesRequestId || !this.isConnected
              || currentVariantId !== this.productForm?.variantIdInput?.value
              || sectionId !== this.dataset.section || productUrl !== this.dataset.url) return;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            if (html.getElementById(`Quantity-Form-${sectionId}`)) this.updateQuantityRules(sectionId, html);
          })
          .catch((error) => { if (!controller.signal.aborted) console.error(error); })
          .finally(() => {
            if (requestId !== this.quantityRulesRequestId) return;
            this.quantityRulesAbortController = null;
            loadingSpinner?.classList.add('hidden');
          });
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        const mainForm = this.querySelector(`[id="product-form-${this.dataset.section}"]`);
        if (mainForm) return mainForm.closest('product-form');
        return Array.from(this.querySelectorAll('product-form')).find(
          (productForm) => !productForm.closest('product-info-upsell')
        );
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
